import CloseIcon from '@mui/icons-material/Close';
import { Alert, AlertTitle, Box, Button, Divider, IconButton, Typography } from '@mui/material';
import useTranslation from 'hooks/useTranslation/useTranslation';
import * as React from 'react';
import LoadingOverlay from 'v2/components/LoadingOverlay/LoadingOverlay';
import { useDrawerContext } from './useDrawerContext';

interface PrimaryAction {
  label: string;
  onClick: () => void;
  form?: string;
  loading?: boolean;
  disabled?: boolean;
  id?: string;
}

interface SecondaryAction {
  label: string;
  onClick?: () => void;
  id?: string;
}

interface DrawerTemplateProps {
  title: string;
  children: React.ReactNode;
  id?: string;
  primaryAction?: PrimaryAction;
  secondaryAction?: SecondaryAction;
  loading?: boolean;
  loadingMessage?: string;
  error?: string;
  errorTitle?: string;
  onDismissError?: () => void;
}

const DrawerTemplate: React.FC<DrawerTemplateProps> = ({
  title,
  children,
  id,
  primaryAction,
  secondaryAction,
  loading,
  loadingMessage,
  error,
  errorTitle,
  onDismissError
}) => {
  const { closeDrawer } = useDrawerContext();
  const { translation } = useTranslation();

  const handleSecondaryClick = () => {
    if (secondaryAction?.onClick) {
      secondaryAction.onClick();
    } else {
      closeDrawer();
    }
  };

  return (
    <LoadingOverlay loading={loading ?? false} message={loadingMessage ?? translation.saving_changes}>
      <Box id={id} sx={{ display: 'flex', flexDirection: 'column', height: '100%', width: 'auto' }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            px: 3,
            py: 2,
            flexShrink: 0
          }}
        >
          <Typography variant="h6">{title}</Typography>
          <IconButton id="drawer-close-button" onClick={closeDrawer} aria-label={translation.close} edge="end">
            <CloseIcon />
          </IconButton>
        </Box>
        <Divider />
        <Box sx={{ flex: 1, overflowY: 'auto', p: 3, display: 'flex', flexDirection: 'column' }}>{children}</Box>
        {error && (
          <Box sx={{ px: 3, py: 2, flexShrink: 0 }}>
            <Alert severity="error" variant="filled" onClose={onDismissError}>
              <AlertTitle>{errorTitle ?? translation.could_not_save_changes}</AlertTitle>
              {error}
            </Alert>
          </Box>
        )}
        <Divider />
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            px: 3,
            py: 2,
            flexShrink: 0
          }}
        >
          <Button id={secondaryAction?.id} variant="text" onClick={handleSecondaryClick}>
            {secondaryAction?.label ?? translation.cancel}
          </Button>
          {primaryAction && (
            <Button
              id={primaryAction.id}
              variant="contained"
              onClick={primaryAction.onClick}
              loading={primaryAction.loading ?? false}
              disabled={primaryAction.disabled ?? false}
              type={primaryAction.form ? 'submit' : 'button'}
              form={primaryAction.form}
            >
              {primaryAction.label}
            </Button>
          )}
        </Box>
      </Box>
    </LoadingOverlay>
  );
};

export default DrawerTemplate;
